import { Component } from '@theme/component';

/**
 * Book an appointment form (sections/sm-appointment.liquid). Sends the booking as a
 * pre-filled WhatsApp message instead of posting it. Before this module runs, the form
 * still opens the WhatsApp chat through its native wa.me action.
 *
 * When the visitor arrives from a product page with ?product=<handle>
 * (blocks/sm-appointment-button.liquid), the product name is filled in and the message
 * links to that product.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - Booking form.
 * @property {HTMLInputElement} name - Visitor's name.
 * @property {HTMLInputElement} product - Product name, prefilled from ?product=.
 * @property {HTMLSelectElement} day - Appointment day.
 * @property {HTMLSelectElement} time - Appointment time.
 *
 * @extends {Component<Refs>}
 */
class SmAppointmentForm extends Component {
  requiredRefs = ['form', 'name', 'product', 'day', 'time'];

  /** @type {AbortController | undefined} */
  #request;

  /** Product looked up from ?product=, used for the message link. @type {{ title: string, url: string } | undefined} */
  #linkedProduct;

  connectedCallback() {
    super.connectedCallback();
    this.#prefillProduct();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#request?.abort();
  }

  /**
   * Opens WhatsApp with the booking details. Runs after native validation passes.
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    const { number, intro, labelName, labelProduct, labelDay, labelTime, labelLink } = this.dataset;
    const product = this.refs.product.value.trim();

    const lines = [intro ?? '', '', `${labelName}: ${this.refs.name.value.trim()}`];
    if (product) lines.push(`${labelProduct}: ${product}`);
    lines.push(`${labelDay}: ${this.refs.day.value}`, `${labelTime}: ${this.refs.time.value}`);
    if (this.#linkedProduct && product === this.#linkedProduct.title) {
      lines.push(`${labelLink}: ${this.#linkedProduct.url}`);
    }

    const url = `https://wa.me/${number}?text=${encodeURIComponent(lines.join('\n'))}`;
    window.open(url, '_blank', 'noopener');
  }

  async #prefillProduct() {
    const handle = new URLSearchParams(window.location.search).get('product');
    if (!handle || this.refs.product.value) return;

    this.#request?.abort();
    this.#request = new AbortController();

    try {
      const root = /** @type {any} */ (window).Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, {
        signal: this.#request.signal,
      });
      if (!response.ok) return;

      /** @type {{ title?: string, url?: string }} */
      const product = await response.json();
      if (!product.title || !product.url) return;

      this.#linkedProduct = {
        title: product.title,
        url: new URL(product.url, window.location.origin).toString(),
      };

      // Keep anything the visitor typed while the lookup was in flight
      if (!this.refs.product.value) this.refs.product.value = product.title;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.warn('[sm-appointment-form] Could not load the product:', error);
    }
  }
}

if (!customElements.get('sm-appointment-form')) {
  customElements.define('sm-appointment-form', SmAppointmentForm);
}
